// SFX playback (IMPLEMENTATION §2.9, research-artAudio §4). Graph:
//   voice (BufferSource → per-voice gain) → duck bus ─┐
//   fanfare voices ──────────────────────────────────┴→ sfx bus (SFX volume) → compressor (−18 dB, 4:1)
//     → master (master volume × mute) → destination;   music bus (v0.2 hook, ducked) → master.
// Buffers: at `attach(ctx)` (the unlock gesture) every rendered take becomes an AudioBuffer synchronously, and
// take 0 of any `engine.unlockSounds` sound the idle prewarm has not reached yet is rendered on the spot, so the
// sound of that same gesture (the cold-open pop) plays. Any other sound still missing its take 0 is rendered on
// its first play (one short render, only before the prewarm completes); later takes are wrapped lazily.
// Anti-fatigue (all numbers from sfx.json engine / juice.json caps.sfxVoices):
//   • retrigger: two starts of the same recipe are ≥ retriggerMs apart (a burst is spread, and dropped once it
//     would be pushed more than maxDeferMs late);
//   • voice caps per recipe and in total: over a cap the OLDEST voice of lower-or-equal priority is faded out
//     (stealFadeMs) exactly when the new one starts — else the new play is dropped. Every voice is scheduled
//     stealFadeMs ahead, so that fade always fits (8 ms of added latency);
//   • seeded take pick (never the same take twice in a row) and ± playbackRate jitter per recipe;
//   • a fanfare ducks the other SFX (and the music bus) by fanfare.duckDb for fanfare.duckMs.
// Times are AudioContext seconds (ctx.currentTime).
import type { JuiceData, SfxData } from '../data/schema.ts';
import type { BankBuilder, RecipeKey, SoundDef, SoundId } from './recipes.ts';

export interface EngineConfig {
  retriggerSec: number;
  maxDeferSec: number;
  stealFadeSec: number;
  volumeRampSec: number;
  duckAttackSec: number;
  duckReleaseSec: number;
  compressor: { thresholdDb: number; ratio: number };
  caps: Readonly<Record<RecipeKey, number>>;
  totalCap: number;
  jitter: Readonly<Record<RecipeKey, number>>;
  priority: Readonly<Record<RecipeKey, number>>;
  /** A fanfare ducks everything else by `db` for `sec`. */
  duck: { db: number; sec: number };
  /** Take 0 of these is rendered inside attach() when missing (the unlock gesture's sounds). */
  unlockSounds: readonly SoundId[];
}

export function engineConfig(sfx: SfxData, juice: Pick<JuiceData, 'caps'>): EngineConfig {
  const e = sfx.engine;
  const { total, ...caps } = juice.caps.sfxVoices;
  return {
    retriggerSec: e.retriggerMs / 1000,
    maxDeferSec: e.maxDeferMs / 1000,
    stealFadeSec: e.stealFadeMs / 1000,
    volumeRampSec: e.volumeRampMs / 1000,
    duckAttackSec: e.duckAttackMs / 1000,
    duckReleaseSec: e.duckReleaseMs / 1000,
    compressor: { thresholdDb: e.compressorThresholdDb, ratio: e.compressorRatio },
    caps,
    totalCap: total,
    jitter: e.jitter,
    priority: e.priority,
    duck: { db: sfx.recipes.fanfare.duckDb, sec: sfx.recipes.fanfare.duckMs / 1000 },
    unlockSounds: e.unlockSounds,
  };
}

export interface PlayRequest {
  sound: SoundId;
  /** playbackRate multiplier before jitter (pitch: 2^(semitones/12)). */
  rate?: number;
  /** Requested start offset from now. */
  delayMs?: number;
  /** Per-voice gain 0..1 (default 1). */
  gain?: number;
}

export interface VoiceInfo {
  sound: SoundId;
  recipe: RecipeKey;
  take: number;
  rate: number;
  start: number;
  end: number;
}

export interface EngineStats {
  played: number;
  /** Refused by the retrigger horizon or a voice cap. */
  dropped: number;
  /** Voices cut short (or cancelled before starting) by a newer voice. */
  stolen: number;
  /** AudioBuffers created so far. */
  buffers: number;
  /** Wall ms spent inside attach() (sample rendering of missing unlock take-0s + AudioBuffer creation). */
  buildMs: number;
  /** Take-0 renders done on a sound's first play (the prewarm had not reached it yet). */
  lazyRenders: number;
}

export interface SfxEngine {
  /** Builds the graph and every available AudioBuffer synchronously. Idempotent. */
  attach(ctx: AudioContext): void;
  attached(): boolean;
  /** Schedules one sound; false when it was dropped (or nothing is attached). */
  play(req: PlayRequest): boolean;
  setVolumes(v: { master?: number; sfx?: number }): void;
  setMuted(m: boolean): void;
  /** Ducking hook (the fanfare uses it; v0.2 music too): the non-lead SFX and the music bus. */
  duck(db: number, sec: number, atDelayMs?: number): void;
  /** v0.2 music: routes `node` through the ducked music bus to master (not through the SFX compressor). */
  connectMusic(node: AudioNode): boolean;
  /** Voices sounding now or scheduled (not stolen/cancelled), oldest first. */
  voices(): VoiceInfo[];
  stats(): EngineStats;
  dispose(): void;
}

interface Voice extends VoiceInfo {
  prio: number;
  gainValue: number;
  src: AudioBufferSourceNode;
  gain: GainNode;
  cancelled: boolean;
}

interface Graph {
  master: GainNode;
  comp: DynamicsCompressorNode;
  sfx: GainNode;
  duck: GainNode;
  music: GainNode;
}

const EPS = 1e-9;

const clamp01 = (v: number): number => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

export interface EngineOptions {
  /** Uniform [0,1): take picks and jitter (seeded; never Math.random in audio/). */
  rand: () => number;
  /** Wall clock in ms (build-cost diagnostics only). */
  clockMs: () => number;
}

export function createSfxEngine(
  cfg: EngineConfig,
  bank: BankBuilder,
  opts: EngineOptions,
): SfxEngine {
  const defs = new Map<SoundId, SoundDef>(bank.sounds.map((d) => [d.id, d]));
  const buffers = new Map<SoundId, AudioBuffer[]>();
  const lastTake = new Map<SoundId, number>();
  const starts = new Map<RecipeKey, number[]>(); // sorted start times per recipe (retrigger)
  let voices: Voice[] = [];
  let ctx: AudioContext | null = null;
  let g: Graph | null = null;
  let vol = { master: 1, sfx: 1 };
  let muted = false;
  const st: EngineStats = {
    played: 0,
    dropped: 0,
    stolen: 0,
    buffers: 0,
    buildMs: 0,
    lazyRenders: 0,
  };

  const bufferFor = (id: SoundId, k: number): AudioBuffer | null => {
    if (!ctx) return null;
    let list = buffers.get(id);
    if (!list) {
      list = [];
      buffers.set(id, list);
    }
    const have = list[k];
    if (have) return have;
    const data = bank.takes(id)[k];
    if (!data) return null;
    const b = ctx.createBuffer(1, data.length, bank.sampleRate);
    b.getChannelData(0).set(data);
    list[k] = b;
    st.buffers += 1;
    return b;
  };

  const masterTarget = (): number => (muted ? 0 : clamp01(vol.master));

  const prune = (now: number): void => {
    voices = voices.filter((v) => !v.cancelled && v.end > now);
    for (const [r, list] of starts) {
      const keep = list.filter((s) => s > now - cfg.retriggerSec - EPS);
      starts.set(r, keep);
    }
  };

  /** Earliest time ≥ want that is ≥ retriggerSec away from every other start of the recipe. */
  const retriggerSlot = (recipe: RecipeKey, want: number): number => {
    let t = want;
    for (const s of starts.get(recipe) ?? []) {
      if (s + cfg.retriggerSec <= t + EPS) continue;
      if (s >= t + cfg.retriggerSec - EPS) break;
      t = s + cfg.retriggerSec;
    }
    return t;
  };

  /**
   * Plans steals so that, with the new voice [t, end), at most `cap` voices matching `match` overlap at
   * any instant. `plan` maps a victim to its new end (≤ its start = cancelled). False: cannot fit.
   */
  const fits = (
    match: (v: Voice) => boolean,
    cap: number,
    t: number,
    end: number,
    prio: number,
    plan: Map<Voice, number>,
  ): boolean => {
    for (;;) {
      const endOf = (v: Voice): number => plan.get(v) ?? v.end;
      const live = voices.filter(
        (v) => match(v) && endOf(v) > v.start + EPS && endOf(v) > t + EPS && v.start < end - EPS,
      );
      const points = [t, ...live.map((v) => v.start).filter((s) => s > t && s < end)];
      const aliveAt = (p: number) => live.filter((v) => v.start <= p + EPS && endOf(v) > p + EPS);
      let peak: Voice[] = [];
      for (const p of points) {
        const a = aliveAt(p);
        if (a.length > peak.length) peak = a;
      }
      if (peak.length < cap) return true;
      // the oldest voice sounding at the busiest instant, if the new one outranks or equals it
      let victim: Voice | null = null;
      for (const v of peak) {
        if (v.prio > prio) continue;
        if (victim === null || v.start < victim.start) victim = v;
      }
      if (victim === null) return false;
      plan.set(victim, Math.max(victim.start, t));
    }
  };

  const steal = (v: Voice, newEnd: number, now: number): void => {
    st.stolen += 1;
    if (newEnd <= v.start + EPS) {
      // scheduled but not started: it never sounds
      v.cancelled = true;
      v.end = v.start;
      v.src.stop(v.start);
      const list = starts.get(v.recipe);
      if (list) {
        const i = list.indexOf(v.start);
        if (i >= 0) list.splice(i, 1);
      }
      return;
    }
    const fadeFrom = Math.max(now, v.start, newEnd - cfg.stealFadeSec);
    v.gain.gain.cancelScheduledValues(fadeFrom);
    v.gain.gain.setValueAtTime(v.gainValue, fadeFrom);
    v.gain.gain.linearRampToValueAtTime(0, newEnd);
    v.src.stop(newEnd);
    v.end = newEnd;
  };

  const duckAt = (db: number, sec: number, at: number): void => {
    if (!g) return;
    const low = 10 ** (db / 20);
    for (const p of [g.duck.gain, g.music.gain]) {
      p.cancelScheduledValues(at);
      p.setTargetAtTime(low, at, cfg.duckAttackSec / 3);
      p.setTargetAtTime(1, at + sec, cfg.duckReleaseSec / 3);
    }
  };

  return {
    attach(c) {
      if (ctx) return;
      const t0 = opts.clockMs();
      ctx = c;
      bank.ensureFirstTakes(cfg.unlockSounds);
      const master = c.createGain();
      master.gain.value = masterTarget();
      master.connect(c.destination);
      const comp = c.createDynamicsCompressor();
      comp.threshold.value = cfg.compressor.thresholdDb;
      comp.ratio.value = cfg.compressor.ratio;
      comp.connect(master);
      const sfx = c.createGain();
      sfx.gain.value = clamp01(vol.sfx);
      sfx.connect(comp);
      const duck = c.createGain();
      duck.connect(sfx);
      const music = c.createGain();
      music.connect(master);
      g = { master, comp, sfx, duck, music };
      for (const d of bank.sounds) {
        const n = bank.takes(d.id).length;
        for (let k = 0; k < n; k++) bufferFor(d.id, k);
      }
      st.buildMs = opts.clockMs() - t0;
    },
    attached: () => ctx !== null,
    play(req) {
      if (!ctx || !g) return false;
      const def = defs.get(req.sound);
      if (!def) throw new Error(`unknown sound ${req.sound}`);
      const recipe = def.recipe;
      const now = ctx.currentTime;
      prune(now);
      // every voice starts stealFadeSec ahead: a voice it steals always has room to fade out (no click)
      const want = now + cfg.stealFadeSec + Math.max(0, req.delayMs ?? 0) / 1000;
      const t = retriggerSlot(recipe, want);
      if (t - want > cfg.maxDeferSec + EPS) {
        st.dropped += 1;
        return false;
      }
      if (bank.takes(req.sound).length === 0) {
        bank.ensureFirstTakes([req.sound]); // first play before the prewarm reached it
        st.lazyRenders += 1;
      }
      const n = bank.takes(req.sound).length;
      let k = Math.min(n - 1, Math.floor(opts.rand() * n));
      if (n > 1 && k === lastTake.get(req.sound)) k = (k + 1) % n;
      const buf = bufferFor(req.sound, k);
      if (!buf) {
        st.dropped += 1;
        return false;
      }
      const j = cfg.jitter[recipe];
      const rate = Math.max(0.05, (req.rate ?? 1) * (1 + j * (2 * opts.rand() - 1)));
      const end = t + buf.length / bank.sampleRate / rate;
      const prio = cfg.priority[recipe];
      const plan = new Map<Voice, number>();
      if (
        !fits((v) => v.recipe === recipe, cfg.caps[recipe], t, end, prio, plan) ||
        !fits(() => true, cfg.totalCap, t, end, prio, plan)
      ) {
        st.dropped += 1;
        return false;
      }
      for (const [v, e] of plan) steal(v, e, now);
      voices = voices.filter((v) => !v.cancelled);

      const gainValue = clamp01(req.gain ?? 1);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = rate;
      const vg = ctx.createGain();
      vg.gain.value = gainValue;
      src.connect(vg);
      vg.connect(recipe === 'fanfare' ? g.sfx : g.duck);
      const voice: Voice = {
        sound: req.sound,
        recipe,
        take: k,
        rate,
        start: t,
        end,
        prio,
        gainValue,
        src,
        gain: vg,
        cancelled: false,
      };
      src.onended = () => {
        src.disconnect();
        vg.disconnect();
        voices = voices.filter((v) => v !== voice);
      };
      src.start(t);
      voices.push(voice);
      const list = starts.get(recipe) ?? [];
      list.push(t);
      list.sort((a, b) => a - b);
      starts.set(recipe, list);
      lastTake.set(req.sound, k);
      st.played += 1;
      if (recipe === 'fanfare') duckAt(cfg.duck.db, cfg.duck.sec, t);
      return true;
    },
    setVolumes(v) {
      vol = { master: v.master ?? vol.master, sfx: v.sfx ?? vol.sfx };
      if (!ctx || !g) return;
      const tc = cfg.volumeRampSec / 3;
      g.master.gain.setTargetAtTime(masterTarget(), ctx.currentTime, tc);
      g.sfx.gain.setTargetAtTime(clamp01(vol.sfx), ctx.currentTime, tc);
    },
    setMuted(m) {
      muted = m;
      if (!ctx || !g) return;
      g.master.gain.setTargetAtTime(masterTarget(), ctx.currentTime, cfg.volumeRampSec / 3);
    },
    duck(db, sec, atDelayMs = 0) {
      if (!ctx) return;
      duckAt(db, sec, ctx.currentTime + atDelayMs / 1000);
    },
    connectMusic(node) {
      if (!g) return false;
      node.connect(g.music);
      return true;
    },
    voices() {
      if (!ctx) return [];
      prune(ctx.currentTime);
      return voices
        .filter((v) => !v.cancelled)
        .sort((a, b) => a.start - b.start)
        .map(({ sound, recipe, take, rate, start, end }) => ({
          sound,
          recipe,
          take,
          rate,
          start,
          end,
        }));
    },
    stats: () => ({ ...st }),
    dispose() {
      for (const v of voices) {
        try {
          v.src.stop();
        } catch {
          // never started / already stopped
        }
      }
      voices = [];
      if (g) {
        for (const n of [g.master, g.comp, g.sfx, g.duck, g.music]) n.disconnect();
      }
      g = null;
      ctx = null; // the context itself belongs to audio/context.ts
    },
  };
}
